import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save, Trash2, Sparkles, Users, PhoneCall } from "lucide-react";
import { fetchNumber, fetchMembers, updateNumber, deleteNumber } from "@/lib/api";
import { initials } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const DELETE_PHRASE = "supprimer mon numéro";

const Section = ({ title, description, icon: Icon, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6">
    <div className="flex items-start gap-3 mb-5">
      <span className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <div>
        <h2 className="font-heading font-semibold text-slate-900">{title}</h2>
        {description && <p className="text-sm text-slate-400 mt-0.5">{description}</p>}
      </div>
    </div>
    {children}
  </div>
);

const NumberSettings = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: number } = useQuery({ queryKey: ["number", id], queryFn: () => fetchNumber(id) });
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: fetchMembers });

  const [form, setForm] = useState(null);
  const [confirmPhrase, setConfirmPhrase] = useState("");

  useEffect(() => {
    if (number) setForm(JSON.parse(JSON.stringify(number)));
  }, [number]);

  const saveMut = useMutation({
    mutationFn: (payload) => updateNumber(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["numbers"] });
      qc.invalidateQueries({ queryKey: ["number", id] });
      qc.invalidateQueries({ queryKey: ["number-members"] });
      toast.success("Modifications enregistrées");
    },
  });

  const delMut = useMutation({
    mutationFn: () => deleteNumber(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["numbers"] });
      toast.success("Numéro supprimé");
      navigate("/parametres");
    },
  });

  if (!form) return <div className="text-sm text-slate-400">Chargement…</div>;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const assigned = members.filter((m) => (form.member_ids || []).includes(m.id));

  const save = () => {
    saveMut.mutate({ line_name: form.line_name });
  };

  return (
    <div data-testid="number-settings-page" className="space-y-6 max-w-4xl">
      <div>
        <Link to="/parametres" data-testid="back-to-settings" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-700 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Retour aux paramètres
        </Link>
        <div className="flex items-center justify-between mt-2">
          <div>
            <h1 className="font-heading font-bold text-slate-900 text-2xl sm:text-3xl tracking-tight">{form.line_name}</h1>
            <p className="text-sm text-slate-500 mt-1 tabular-nums" data-testid="line-number">{form.number}</p>
          </div>
          <button data-testid="save-number-btn" onClick={save} disabled={saveMut.isPending} className="hidden md:inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold px-5 py-2.5 hover:bg-emerald-700 transition-colors disabled:opacity-50">
            <Save className="h-4 w-4" /> Enregistrer
          </button>
        </div>
      </div>

      {/* Line name */}
      <Section title="Nom de la ligne" description="Nom d'affichage personnalisé pour ce numéro." icon={Sparkles}>
        <Label className="text-sm">Nom affiché</Label>
        <Input data-testid="line-name-input" value={form.line_name} onChange={(e) => set({ line_name: e.target.value })} className="mt-1 max-w-md" />
      </Section>

      {/* Assigned members */}
      <Section title="Membres assignés" description="Membres de l'équipe rattachés à cette ligne." icon={Users}>
        {assigned.length === 0 ? (
          <p className="text-sm text-slate-400 italic" data-testid="line-members-empty">Aucun membre assigné.</p>
        ) : (
          <div className="space-y-1.5 max-w-md" data-testid="line-members-list">
            {assigned.map((m) => (
              <div key={m.id} data-testid={`line-member-${m.id}`} className="flex items-center gap-2.5 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                <span className="h-7 w-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[10px] font-semibold text-slate-500 shrink-0">
                  {initials(m.name)}
                </span>
                <div className="min-w-0 flex-1 leading-tight">
                  <p className="text-sm font-medium text-slate-800 truncate">{m.name}</p>
                  <p className="text-[11px] text-slate-400 truncate">{m.email}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Routing: managed on the phone server, read-only note */}
      <div data-testid="routing-readonly-note" className="rounded-xl border border-slate-200 bg-slate-50 p-5 flex items-start gap-3">
        <span className="h-9 w-9 rounded-lg bg-white border border-slate-200 text-slate-400 flex items-center justify-center shrink-0">
          <PhoneCall className="h-[18px] w-[18px]" />
        </span>
        <p className="text-sm text-slate-600 mt-1.5">
          Routage des appels : annonce d'accueil → sonnerie de l'équipe → messagerie (géré sur le standard)
        </p>
      </div>

      {/* Delete */}
      <div className="rounded-xl border border-red-200 bg-red-50/40 p-6" data-testid="delete-number-section">
        <div className="flex items-start gap-3 mb-4">
          <span className="h-9 w-9 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0"><Trash2 className="h-[18px] w-[18px]" /></span>
          <div>
            <h2 className="font-heading font-semibold text-red-800">Supprimer le numéro</h2>
            <p className="text-sm text-red-600/80 mt-0.5">
              Cette action est irréversible. Pour confirmer, saisissez « <span className="font-semibold">{DELETE_PHRASE}</span> » ci-dessous.
            </p>
          </div>
        </div>
        <Input data-testid="delete-confirm-input" value={confirmPhrase} onChange={(e) => setConfirmPhrase(e.target.value)} placeholder={DELETE_PHRASE} className="max-w-md mb-3 bg-white" />
        <button
          data-testid="delete-number-btn"
          disabled={confirmPhrase.trim().toLowerCase() !== DELETE_PHRASE || delMut.isPending}
          onClick={() => delMut.mutate()}
          className="inline-flex items-center gap-2 rounded-lg bg-red-600 text-white text-sm font-semibold px-5 py-2.5 hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Trash2 className="h-4 w-4" /> Supprimer définitivement
        </button>
      </div>

      {/* Mobile: Save fixed at the bottom */}
      <div className="md:hidden sticky bottom-0 -mx-4 px-4 py-3 bg-white border-t border-slate-200 pb-safe z-10">
        <button
          data-testid="save-number-btn-mobile"
          onClick={save}
          disabled={saveMut.isPending}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold py-3 hover:bg-emerald-700 transition-colors disabled:opacity-50"
        >
          <Save className="h-4 w-4" /> Enregistrer
        </button>
      </div>
    </div>
  );
};

export default NumberSettings;
